/**
 * Cmd-K spotlight — natural-language search that routes to pages, tickers,
 * or passes through to chat for ambiguous queries.
 *
 * R8: replaces the old static focal list with intent-aware routing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Item {
  label: string;
  path: string;
  icon?: string;
}

const ROUTES: Item[] = [
  { label: 'Today (Morning Brief)', path: '/today', icon: '☀' },
  { label: 'Portfolio', path: '/portfolio', icon: '📊' },
  { label: 'Research', path: '/research', icon: '🔬' },
  { label: 'New thesis (wizard)', path: '/research?wizard=new', icon: '➕' },
  { label: 'Approvals', path: '/approvals', icon: '✓' },
  { label: 'Settings', path: '/settings', icon: '⚙' },
];

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
      setQ('');
    }
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const v = q.trim();
    if (!v) return ROUTES;
    const lc = v.toLowerCase();
    const sym = v.toUpperCase();
    const tickerMatch = /^[A-Z]{1,5}$/.test(sym);
    const out: Item[] = [];
    if (tickerMatch) {
      out.push({ label: `Chart ${sym}`, path: `/portfolio?symbol=${sym}`, icon: '📈' });
    }
    for (const r of ROUTES) {
      if (r.label.toLowerCase().includes(lc)) out.push(r);
    }

    // Natural-language shortcuts
    if (lc.includes('approve') || lc.includes('pending')) {
      if (!out.some((i) => i.path === '/approvals')) {
        out.unshift({ label: 'Approvals', path: '/approvals', icon: '✓' });
      }
    }
    if (lc.includes('performance') || lc.includes('return') || lc.includes('pnl')) {
      out.unshift({ label: 'Portfolio (performance)', path: '/portfolio', icon: '📊' });
    }
    if (lc.includes('thesis') || lc.includes('new thesis')) {
      out.unshift({ label: 'New thesis (wizard)', path: '/research?wizard=new', icon: '➕' });
    }

    return out;
  }, [q]);

  const choose = useCallback(
    (it: Item) => {
      navigate(it.path);
      setOpen(false);
    },
    [navigate],
  );

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)}>
      <div className="card w-full max-w-md p-2 mx-4" role="dialog" aria-label="command palette" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="input w-full"
          placeholder="Go to page, ticker (NVDA), or action..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && items[0]) choose(items[0]);
          }}
        />
        <ul className="mt-2 space-y-0.5 max-h-64 overflow-y-auto">
          {items.map((it, i) => (
            <li key={i}>
              <button
                className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-bg-elev flex items-center gap-2"
                onClick={() => choose(it)}
              >
                {it.icon && <span className="text-sm">{it.icon}</span>}
                {it.label}
              </button>
            </li>
          ))}
          {items.length === 0 && (
            <li className="text-xs text-ink-mute px-2 py-1">no matches — try a ticker symbol or page name</li>
          )}
        </ul>
        <div className="text-[10px] text-ink-dim px-2 py-1 border-t border-edge mt-1">
          ⌘K · Enter to select · Esc to close
        </div>
      </div>
    </div>
  );
}
